import { useState } from 'react';
import { useCircleStore } from '../stores/circle';
import { InviteQR } from '../components/circle/InviteQR';
import { MemberList } from '../components/circle/MemberList';
import { useAuthStore } from '../stores/auth';
import { toast } from '../components/ui/Toast';
import { useNavigate } from 'react-router-dom';

export function CirclePage() {
  const circle = useCircleStore((s) => s.circle);
  const members = useCircleStore((s) => s.members);
  const regenInvite = useCircleStore((s) => s.regenInvite);
  const renameCircle = useCircleStore((s) => s.renameCircle);
  const leaveCircle = useCircleStore((s) => s.leaveCircle);
  const me = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  if (!circle) return null;
  const inviteUrl = `${window.location.origin}/join/${circle.invite_code}`;
  const isOwner = members.find((m) => m.user_id === me?.id)?.role === 'owner';

  async function saveName() {
    const trimmed = nameDraft.trim();
    if (!trimmed || !circle || trimmed === circle.name) {
      setEditingName(false);
      return;
    }
    try {
      await renameCircle(trimmed);
      toast('Circle renamed', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not rename', 'error');
    } finally {
      setEditingName(false);
    }
  }

  async function regen() {
    if (!window.confirm('Regenerate invite link? The old one will stop working.')) return;
    setBusy(true);
    try {
      await regenInvite();
      toast('New invite generated', 'success');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not regenerate', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function leave() {
    if (!window.confirm('Leave this circle?')) return;
    try {
      await leaveCircle();
      navigate('/onboarding/circle', { replace: true });
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not leave', 'error');
    }
  }

  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-text-muted">Circle name</h2>
        <div className="flex items-center gap-2 rounded-2xl border border-border-subtle bg-card px-4 py-3">
          {editingName ? (
            <>
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void saveName();
                  if (e.key === 'Escape') setEditingName(false);
                }}
                maxLength={40}
                className="flex-1 bg-transparent text-sm text-text-primary outline-none"
              />
              <button onClick={() => void saveName()} className="text-xs text-sage">Save</button>
              <button onClick={() => setEditingName(false)} className="text-xs text-text-muted">Cancel</button>
            </>
          ) : (
            <>
              <span className="flex-1 text-sm text-text-primary">{circle.name}</span>
              {isOwner && (
                <button
                  onClick={() => { setNameDraft(circle.name); setEditingName(true); }}
                  className="text-xs text-text-muted"
                  aria-label="Rename circle"
                >
                  Edit
                </button>
              )}
            </>
          )}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-text-muted">Members ({members.length}/{circle.max_members})</h2>
        <MemberList />
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-text-muted">Invite</h2>
        <InviteQR url={inviteUrl} />
        {isOwner && (
          <button
            onClick={regen}
            disabled={busy}
            className="mt-3 w-full rounded-lg border border-border-subtle bg-surface py-2 text-sm text-text-muted disabled:opacity-50"
          >
            Regenerate invite link
          </button>
        )}
      </section>

      <section>
        <button
          onClick={leave}
          className="w-full rounded-lg border border-rose/40 bg-rose/10 py-2 text-sm text-rose"
        >
          Leave circle
        </button>
      </section>
    </div>
  );
}
